import { Component, type ComponentChildren } from "preact";
import { track } from "./lib/analytics";

type Props = { children: ComponentChildren };
type State = { crashed: boolean };

/**
 * Top-level error boundary.
 *
 * A render or lifecycle crash anywhere in the three steps is caught here and
 * replaced with a fallback that keeps the human-contact path open, rather than
 * leaving a white iframe on the marketing site's modal with no way out. That
 * matters more in embed mode than standalone: the footer's phone numbers are not
 * rendered there, so a crash with no fallback strands the customer completely.
 *
 * THE FALLBACK MAY NOT SAY WHAT WAS SENT. It wraps <App/> at the root, so it
 * also catches a crash on the way into step 3, by which point submitLead has
 * resolved and the lead has reached n8n, the inbox and the CRM. It used to read
 * "Nothing you entered was sent anywhere", which was false in exactly that
 * window, and the copy gate could not see it because the sentence contains none
 * of FORBIDDEN_COPY_WORDS. Cleared in docs/copy-review-2026-09-11.md and pinned
 * by a named test; grep SHELL-COPY-2026-09-11 for both ends.
 *
 * The report carries NO parameters. `track()`'s allowlist would strip anything
 * PII-shaped anyway, but an error message is free text originating from a stack
 * that has held the entered address in scope, so none of it is offered to GA4 in
 * the first place. The event's existence and its count are the signal; the
 * detail belongs in Cloud Logging on the functions side.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  componentDidCatch(): void {
    track("app_exception");
  }

  render() {
    if (!this.state.crashed) return this.props.children;

    return (
      <div class="error-fallback" role="alert">
        <h1>Something went wrong</h1>
        {/* SHELL-COPY-2026-09-11. Makes no claim about what was or was not
            sent. This boundary wraps the whole app, so it catches crashes that
            happen after submitLead has already resolved. Guarded by a test. */}
        <p>
          We hit an unexpected problem on our end. Reach a Kasselman Solar
          advisor and we will check the address for you.
        </p>
        <p class="error-fallback-contact">
          <a href="tel:+15184788365">Upstate (518) 478-8365</a>
          <span class="sep" aria-hidden="true">·</span>
          <a href="tel:+18456814088">Hudson Valley (845) 681-4088</a>
          <span class="sep" aria-hidden="true">·</span>
          <a href="mailto:info@kasselmansolar.com">info@kasselmansolar.com</a>
        </p>
        <button type="button" class="primary" onClick={() => window.location.reload()}>
          Start over
        </button>
      </div>
    );
  }
}
